const AUTO_SCROLL_THRESHOLD = 100; // Distance from viewport edge to trigger auto-scroll (in pixels)
const AUTO_SCROLL_STEP = 10;

export const EDITOR_NODE_DRAG_TYPE = 'application/x-new-blog-editor-node';
export const EDITOR_ROOT_NODE_DRAG_TYPE = 'application/x-new-blog-editor-root-node';
export const EMPTY_DROP_TARGET = Object.freeze({ index: null, position: null });

export const hasDragType = (dataTransfer, type) => {
    const types = dataTransfer?.types;
    if (!types) return false;

    return typeof types.includes === 'function'
        ? types.includes(type)
        : Array.from(types).includes(type);
};

export const writeEditorDragData = (dataTransfer, { nodeId, text = nodeId, root = false }) => {
    if (!dataTransfer || !nodeId) return false;

    dataTransfer.effectAllowed = 'move';
    dataTransfer.setData(EDITOR_NODE_DRAG_TYPE, nodeId);
    dataTransfer.setData('text/plain', String(text));
    if (root) dataTransfer.setData(EDITOR_ROOT_NODE_DRAG_TYPE, nodeId);
    return true;
};

export const readEditorDragNodeId = (dataTransfer) => (
    dataTransfer?.getData?.(EDITOR_NODE_DRAG_TYPE) || ''
);

export const updateDropTarget = (setDropTarget, nextTarget) => {
    setDropTarget((current) => (
        current.index === nextTarget.index && current.position === nextTarget.position
            ? current
            : nextTarget
    ));
};

const DROP_EDGE_RATIO = 0.25;

export const getSiblingDropPosition = ({ pointerX, pointerY, width, height }) => {
    const safeWidth = Math.max(1, Number(width) || 0);
    const safeHeight = Math.max(1, Number(height) || 0);
    const resolvedX = Number.isFinite(Number(pointerX)) ? Number(pointerX) : safeWidth / 2;
    const resolvedY = Number.isFinite(Number(pointerY)) ? Number(pointerY) : safeHeight / 2;

    if (resolvedY < safeHeight * DROP_EDGE_RATIO) return 'above';
    if (resolvedY > safeHeight * (1 - DROP_EDGE_RATIO)) return 'below';
    return resolvedX < safeWidth / 2 ? 'left' : 'right';
};

export const getNestedDropPosition = ({
    pointerX,
    pointerY,
    width,
    height,
    canHaveChildren,
}) => {
    if (pointerY < height * 0.25) {
        return 'before';
    }

    if (pointerY > height * 0.75 || !canHaveChildren) {
        if (pointerY > height * 0.75) return 'after';
        return Number(pointerX) < Number(width) / 2 ? 'left' : 'right';
    }

    if (Number(pointerX) < Number(width) * 0.25) return 'left';
    if (Number(pointerX) > Number(width) * 0.75) return 'right';
    return 'child';
};

export const nestedDropOperation = (position) => {
    if (position === 'left') return 'before';
    if (position === 'right') return 'after';
    return position;
};

export const stopAutoScroll = (autoScrollFrameRef, autoScrollDirectionRef) => {
    autoScrollDirectionRef.current = 0;

    if (autoScrollFrameRef.current !== null) {
        cancelAnimationFrame(autoScrollFrameRef.current);
        autoScrollFrameRef.current = null;
    }
};

export const startAutoScroll = (direction, autoScrollFrameRef, autoScrollDirectionRef) => {
    if (autoScrollDirectionRef.current === direction && autoScrollFrameRef.current !== null) {
        return;
    }

    stopAutoScroll(autoScrollFrameRef, autoScrollDirectionRef);
    autoScrollDirectionRef.current = direction;

    const step = () => {
        if (autoScrollDirectionRef.current === 0) {
            autoScrollFrameRef.current = null;
            return;
        }

        window.scrollBy({ top: autoScrollDirectionRef.current * AUTO_SCROLL_STEP, left: 0, behavior: 'instant' });
        autoScrollFrameRef.current = requestAnimationFrame(step);
    };

    autoScrollFrameRef.current = requestAnimationFrame(step);
};

export const updateAutoScroll = (clientY, autoScrollFrameRef, autoScrollDirectionRef) => {
    if (clientY <= AUTO_SCROLL_THRESHOLD) {
        startAutoScroll(-1, autoScrollFrameRef, autoScrollDirectionRef);
        return;
    }

    if (clientY >= window.innerHeight - AUTO_SCROLL_THRESHOLD) {
        startAutoScroll(1, autoScrollFrameRef, autoScrollDirectionRef);
        return;
    }

    stopAutoScroll(autoScrollFrameRef, autoScrollDirectionRef);
};

export const moveItemWithKeys = ({
    jsonData,
    itemKeys,
    fromIndex,
    toIndex,
    captureItemPositions,
}) => {
    if (!jsonData || !jsonData.content || fromIndex === toIndex) {
        return null;
    }

    if (fromIndex < 0 || toIndex < 0 || fromIndex >= jsonData.content.length || toIndex >= jsonData.content.length) {
        return null;
    }

    captureItemPositions();

    const nextJsonData = structuredClone(jsonData);
    const [movedItem] = nextJsonData.content.splice(fromIndex, 1);
    nextJsonData.content.splice(toIndex, 0, movedItem);

    if (fromIndex >= itemKeys.length || toIndex >= itemKeys.length) {
        return {
            nextJsonData,
            nextItemKeys: itemKeys,
        };
    }

    const nextItemKeys = [...itemKeys];
    const [movedKey] = nextItemKeys.splice(fromIndex, 1);
    nextItemKeys.splice(toIndex, 0, movedKey);

    return {
        nextJsonData,
        nextItemKeys,
    };
};

export const handleDragOver = ({
    event,
    targetIndex,
    draggedIndex,
    setDropTarget,
    updateAutoScrollFn,
    stopAutoScrollFn,
}) => {
    event.preventDefault();

    updateAutoScrollFn(event.clientY);

    if (draggedIndex === null || draggedIndex === targetIndex) {
        updateDropTarget(setDropTarget, EMPTY_DROP_TARGET);
        stopAutoScrollFn();
        return;
    }

    const targetRect = event.currentTarget.getBoundingClientRect();
    const pointerX = event.clientX - targetRect.left;
    const pointerY = event.clientY - targetRect.top;
    const position = getSiblingDropPosition({
        pointerX,
        pointerY,
        width: targetRect.width,
        height: targetRect.height,
    });

    updateDropTarget(setDropTarget, { index: targetIndex, position });
};

export const handleDragOverHorizontal = ({
    event,
    targetIndex,
    draggedIndex,
    setDropTarget,
    updateAutoScrollFn,
    stopAutoScrollFn,
}) => {
    event.preventDefault();

    updateAutoScrollFn(event.clientY);

    if (draggedIndex === null || draggedIndex === targetIndex) {
        updateDropTarget(setDropTarget, EMPTY_DROP_TARGET);
        stopAutoScrollFn();
        return;
    }

    const targetRect = event.currentTarget.getBoundingClientRect();
    const pointerX = event.clientX - targetRect.left;
    const position = pointerX < targetRect.width / 2 ? 'left' : 'right';

    updateDropTarget(setDropTarget, { index: targetIndex, position });
};

export const handleDrop = ({
    event,
    targetIndex,
    draggedIndex,
    dropTarget,
    moveItem,
    setDraggedIndex,
    setDropTarget,
    stopAutoScrollFn,
    beforePosition = 'above',
}) => {
    event.preventDefault();
    stopAutoScrollFn();

    if (draggedIndex === null || draggedIndex === targetIndex) {
        setDraggedIndex(null);
        updateDropTarget(setDropTarget, EMPTY_DROP_TARGET);
        return;
    }

    let dropPosition = dropTarget.index === targetIndex ? dropTarget.position : null;

    // State updates during drag can lag one frame; derive position directly from pointer on drop.
    if (!dropPosition && event.currentTarget) {
        const targetRect = event.currentTarget.getBoundingClientRect();
        dropPosition = getSiblingDropPosition({
            pointerX: event.clientX - targetRect.left,
            pointerY: event.clientY - targetRect.top,
            width: targetRect.width,
            height: targetRect.height,
        });
    }

    if (!dropPosition) {
        dropPosition = beforePosition === 'left' ? 'right' : 'below';
    }

    const insertBefore = dropPosition === beforePosition
        || dropPosition === 'above'
        || dropPosition === 'left';

    let newIndex = insertBefore ? targetIndex : targetIndex + 1;
    if (draggedIndex < newIndex) {
        newIndex -= 1;
    }

    moveItem(draggedIndex, newIndex);
    setDraggedIndex(null);
    updateDropTarget(setDropTarget, EMPTY_DROP_TARGET);
};

export const handleDragEnd = ({ stopAutoScrollFn, setDraggedIndex, setDropTarget }) => {
    stopAutoScrollFn();
    setDraggedIndex(null);
    updateDropTarget(setDropTarget, EMPTY_DROP_TARGET);
};
